'use client';

import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/shadcn/popover';
import { Button } from '@/shared/ui/shadcn/button';
import { Label } from '@/shared/ui/shadcn/label';
import { cn } from '@/shared/lib/cn';

interface ColorPickerPopoverProps {
  color: string;
  onChange: (color: string) => void;
  title?: string;
  trigger?: React.ReactNode;
}

export function ColorPickerPopover({
  color,
  onChange,
  title = 'Select Color',
  trigger,
}: ColorPickerPopoverProps) {
  const [tempColor, setTempColor] = useState(color);
  const [open, setOpen] = useState(false);
  const [isModalReady, setIsModalReady] = useState(false);

  useEffect(() => {
    setTempColor(color);
  }, [color]);

  // Prevent native color picker from opening immediately on modal open
  useEffect(() => {
    if (open) {
      setIsModalReady(false);
      // Longer delay to prevent click event from propagating to color input on iOS
      const timer = setTimeout(() => setIsModalReady(true), 500);
      return () => clearTimeout(timer);
    } else {
      setIsModalReady(false);
      return undefined;
    }
  }, [open]);

  const handleApply = () => {
    onChange(tempColor);
    setOpen(false);
  };

  const handleCancel = () => {
    setTempColor(color); // Reset to original color
    setOpen(false);
  };

  // Architectural colors - muted and sophisticated tones suitable for facade design
  const presetColors = [
    // Whites and light grays
    '#FFFFFF',
    '#F8F8F8',
    '#F0F0F0',
    '#E8E8E8',
    '#D8D8D8',
    '#C8C8C8',
    '#B0B0B0',
    '#989898',
    // Warm grays and beiges
    '#F5F3F0',
    '#EAE7E3',
    '#DED9D3',
    '#D2CCC4',
    '#C4BDB4',
    '#B5ADA3',
    '#A69B8F',
    '#8B7F72',
    // Cool grays
    '#E8EAEC',
    '#D8DCDF',
    '#C7CDD2',
    '#B4BAC0',
    '#9FA6AD',
    '#8A929A',
    '#6F7780',
    '#565D65',
    // Muted blues
    '#E3E8EC',
    '#D1DADF',
    '#BFCAD2',
    '#A8B7C2',
    '#90A3B0',
    '#768A98',
    '#5D7080',
    '#4A5A68',
    // Muted greens
    '#E5E8E3',
    '#D7DCD3',
    '#C5CDC1',
    '#B0BBAA',
    '#97A68F',
    '#7E9074',
    '#65775B',
    '#4D5E45',
    // Sage and olive
    '#E4E7E2',
    '#D4DAD0',
    '#C2CABC',
    '#ADB7A5',
    '#96A28C',
    '#7D8A70',
    '#647155',
    '#4C5841',
    // Yellows and golds
    '#FFF9E6',
    '#FFF3CC',
    '#FFECB3',
    '#FFE599',
    '#FFDB66',
    '#FFCD3D',
    '#F4BE00',
    '#C99700',
    // Muted terracotta and clay
    '#E8E3DF',
    '#DCD3CA',
    '#CCC0B3',
    '#BAAA9A',
    '#A6927F',
    '#8E7764',
    '#755D4B',
    '#5D4638',
    // Muted browns
    '#E6E2DD',
    '#D9D2C9',
    '#C8BDB0',
    '#B5A695',
    '#9E8D79',
    '#86735D',
    '#6D5A43',
    '#55442D',
    // Charcoals and darks
    '#60605E',
    '#52524F',
    '#454541',
    '#393934',
    '#2D2D28',
    '#22221D',
    '#181813',
    '#0F0F0C',
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger || (
          <Button variant="outline" className="w-full justify-start">
            <div
              className="w-4 h-4 rounded border mr-2"
              style={{ backgroundColor: color }}
            />
            {color}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-96" align="start">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h3 className="text-card-title">{title}</h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleCancel}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Native color picker */}
          <div className="space-y-2">
            <Label htmlFor="color-input">Color Picker</Label>
            <input
              id="color-input"
              type="color"
              value={tempColor}
              onChange={(e) => setTempColor(e.target.value)}
              onClick={(e) => {
                // Prevent native picker from opening if modal just opened
                if (!isModalReady) {
                  e.preventDefault();
                  e.stopPropagation();
                  return false;
                }
                return undefined;
              }}
              onPointerDown={(e) => {
                // Prevent native picker from opening if modal just opened
                if (!isModalReady) {
                  e.preventDefault();
                  e.stopPropagation();
                  return false;
                }
                return undefined;
              }}
              onTouchStart={(e) => {
                // Prevent native picker from opening if modal just opened (iOS specific)
                if (!isModalReady) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              onFocus={(e) => {
                // Prevent focus from triggering picker on iOS
                if (!isModalReady) {
                  e.target.blur();
                }
              }}
              className={cn(
                'w-full h-12 rounded-lg border border-border transition-all',
                isModalReady ? 'cursor-pointer' : 'pointer-events-none'
              )}
            />
          </div>

          {/* Preset colors */}
          <div className="space-y-2">
            <Label>Preset Colors</Label>
            <div className="grid grid-cols-8 gap-1">
              {presetColors.map((presetColor) => (
                <button
                  key={presetColor}
                  onClick={() => setTempColor(presetColor)}
                  className={`
                    w-full aspect-square rounded-md border-2 transition-all
                    ${
                      tempColor === presetColor
                        ? 'border-primary scale-105 ring-2 ring-primary/20'
                        : 'border-border hover:border-muted-foreground'
                    }
                  `}
                  style={{ backgroundColor: presetColor }}
                  title={presetColor}
                />
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={handleCancel} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleApply} className="flex-1">
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
